import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import MicDictation from "../components/MicDictation";
import { useMusicPlayer } from "../context/MusicPlayerContext";
import PageMeta from "../components/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = "rm_chat_session_id";
const AUTO_SPEAK_KEY = "rm_chat_auto_speak";
const TTS_VOICE_KEY = "rm_chat_tts_voice";

// OpenAI TTS voice options — six distinct voices the OS can speak in.
// `fable` (a thoughtful, British-inflected male voice) is the default
// because it best fits the OS's gentle-teacher tone. Recipients can
// pick a different timbre that lands better for them; the choice
// persists across the session via localStorage.
//
// Voices: alloy (warm-neutral), echo (clear-male), fable (thoughtful-
// British-male), onyx (deep-male), nova (bright-female), shimmer
// (soft-warm-feminine).
const TTS_VOICE_OPTIONS = [
  { id: "fable", label: "Fable · thoughtful teacher" },
  { id: "alloy", label: "Alloy · warm neutral" },
  { id: "shimmer", label: "Shimmer · soft feminine" },
  { id: "nova", label: "Nova · bright feminine" },
  { id: "echo", label: "Echo · clear masculine" },
  { id: "onyx", label: "Onyx · deep masculine" },
];
const TTS_VOICE_IDS = TTS_VOICE_OPTIONS.map((v) => v.id);
// Fable is the locked default — see header comment above. NEVER change
// without an explicit user direction.
const DEFAULT_TTS_VOICE = "onyx";

function loadStoredVoice() {
  try {
    const v = localStorage.getItem(TTS_VOICE_KEY);
    return TTS_VOICE_IDS.includes(v) ? v : DEFAULT_TTS_VOICE;
  } catch {
    return DEFAULT_TTS_VOICE;
  }
}

// ───────────────────────── tiny helpers ──────────────────────────

// Turn "**bold**" markdown into bold spans and preserve line breaks.
// The model's replies are short prose — no full markdown pipeline needed.
function renderReply(text) {
  const paragraphs = (text || "").split(/\n\n+/).filter((p) => p.trim());
  return paragraphs.map((para, i) => {
    const lines = para.split("\n").filter((l) => l.trim());
    return (
      <p key={i} className="rm-chat-para">
        {lines.map((line, j) => (
          <span key={j}>
            {renderInlineBold(line)}
            {j < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });
}

function renderInlineBold(line) {
  // Split on **bold** first, then within each non-bold chunk split on
  // single-asterisk *italic*. Model output sometimes uses *word* for
  // gentle emphasis (e.g. "the *yes* to the lie") — render as italic so
  // the asterisks don't leak through as literal characters.
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.flatMap((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return [<strong key={`b-${i}`}>{p.slice(2, -2)}</strong>];
    }
    // italic pass on the non-bold remainder
    return p.split(/(\*[^*\n]+\*)/g).map((q, j) => {
      if (q.startsWith("*") && q.endsWith("*") && q.length > 2) {
        return <em key={`i-${i}-${j}`}>{q.slice(1, -1)}</em>;
      }
      return <span key={`t-${i}-${j}`}>{q}</span>;
    });
  });
}

// ───────────────────────── component ──────────────────────────

export default function Chat() {
  // Pause the global background music whenever the user opens the
  // hands-free phone-icon convo or taps the dictation mic — without
  // this, the song plays through the device speaker and bleeds into
  // the recording (Whisper transcribes the lyrics). User direction,
  // Feb 2026.
  const { pause: pauseMusic } = useMusicPlayer();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  // Voice-out state — auto-speak toggle persists across sessions; the
  // currently-playing index lets us show a "playing" indicator AND
  // guarantees only one audio element plays at a time. Default OFF for
  // text-mode chat — the user can tap "🔇 Voice off" to switch it on.
  // Voice/convo mode flips it on its own when the orb starts a session.
  const [autoSpeak, setAutoSpeak] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(AUTO_SPEAK_KEY);
    return stored === null ? false : stored === "1";
  });
  // Feb 2026 (user direction): the OS now offers six voice options.
  // The chosen voice persists across the session via localStorage.
  const [ttsVoice, setTtsVoice] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_TTS_VOICE;
    return loadStoredVoice();
  });
  const [playingIdx, setPlayingIdx] = useState(-1);
  const audioRef = useRef(null);
  // Packet upload — silent context for the OS. When set, the chat
  // header shows a small "Walking with: <name> • <rung>" badge so the
  // user knows the OS has their packet, without the OS ever quoting it.
  const [packetCtx, setPacketCtx] = useState(null); // {name, rung, filename}
  const [uploadingPacket, setUploadingPacket] = useState(false);
  const packetFileRef = useRef(null);
  // Monotonic TTS generation counter — every new speakMessage() bumps it.
  // When a pending TTS response lands we check its gen against this ref;
  // if superseded we drop the response on the floor. Prevents two voices
  // overlapping if the user fires a second turn (or if React StrictMode
  // double-invokes an updater).
  const ttsGenRef = useRef(0);
  // Ref because sendMessage closes over state and React strict-mode can
  // spring two playbacks otherwise.
  const autoSpeakRef = useRef(autoSpeak);
  useEffect(() => { autoSpeakRef.current = autoSpeak; }, [autoSpeak]);

  // Strip markdown formatting before computing "spoken" text — avoids
  // the voice saying "asterisk" and makes long replies shorter.
  const toSpoken = (txt) => (txt || "").replace(/\*+/g, "").trim();

  // Play one reply through the TTS endpoint. Called automatically after
  // every assistant reply (when autoSpeak is on) and manually via the
  // small speaker icon on each assistant message.
  //
  // The optional `convoMode` flag forces playback even when the global
  // "Voice off" toggle is set AND guarantees the conversation-loop
  // always re-opens the mic afterward, regardless of whether playback
  // succeeded, failed, or was blocked by the browser's autoplay policy.
  async function speakMessage(idx, text, forConvo = false) {
    const spoken = toSpoken(text);
    const continueLoop = () => {
      if (forConvo && convoActiveRef.current) {
        // Small delay so the TTS tail doesn't bleed into the mic.
        setTimeout(() => startWhisperRecording(), 250);
      }
    };
    if (!spoken) {
      continueLoop();
      return;
    }
    // Stop any currently-playing audio FIRST, then claim a fresh
    // generation number. Earlier we bumped gen before stopSpeaking, but
    // stopSpeaking itself increments ttsGenRef — which meant the gen
    // check after `await` always failed and TTS was silently discarded
    // (and the convo-loop never reopened the mic). Order matters here.
    stopSpeaking();
    const gen = ++ttsGenRef.current;
    setPlayingIdx(idx);
    let audio = null;
    let url = null;
    try {
      const resp = await axios.post(
        `${API}/chat/tts`,
        { text: spoken, voice: ttsVoice },
        { responseType: "blob" }
      );
      if (gen !== ttsGenRef.current) return;
      url = URL.createObjectURL(resp.data);
      audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPlayingIdx(-1);
        }
        if (url) URL.revokeObjectURL(url);
        continueLoop();
      };
      audio.onerror = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPlayingIdx(-1);
        }
        if (url) URL.revokeObjectURL(url);
        continueLoop();
      };
      try {
        await audio.play();
      } catch (_playErr) {
        // Some mobile browsers (Safari, some Android webviews) reject
        // .play() if it's called from an async callback detached from
        // the original user gesture. When that happens TTS can't play,
        // but we must still keep the conversation alive.
        if (audioRef.current === audio) audioRef.current = null;
        if (gen === ttsGenRef.current) setPlayingIdx(-1);
        if (url) URL.revokeObjectURL(url);
        continueLoop();
      }
    } catch (_e) {
      // TTS API call itself failed — keep the conversation going.
      if (audioRef.current === audio) audioRef.current = null;
      if (gen === ttsGenRef.current) setPlayingIdx(-1);
      if (url) URL.revokeObjectURL(url);
      continueLoop();
    }
  }

  function stopSpeaking() {
    // Bump generation so any in-flight TTS response gets discarded.
    ttsGenRef.current += 1;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
    setPlayingIdx(-1);
  }

  function toggleAutoSpeak() {
    const next = !autoSpeak;
    setAutoSpeak(next);
    try { localStorage.setItem(AUTO_SPEAK_KEY, next ? "1" : "0"); } catch { /* noop */ }
    if (!next) stopSpeaking();
  }

  function changeTtsVoice(voiceId) {
    if (!TTS_VOICE_IDS.includes(voiceId)) return;
    // If a reply is currently being spoken, interrupt it so the next
    // playback uses the freshly-chosen voice (otherwise the change
    // looks "broken" until the user fires another turn).
    stopSpeaking();
    setTtsVoice(voiceId);
    try { localStorage.setItem(TTS_VOICE_KEY, voiceId); } catch { /* noop */ }
  }

  // ─── Voice Conversation Mode (hands-free, ChatGPT-style) ────────
  // When active, the browser's SpeechRecognition listens continuously;
  // each end-of-speech automatically sends the transcript as a user
  // message. The assistant's reply is auto-spoken, and when the TTS
  // ends we re-open the mic for the next turn. User exits by tapping
  // the big End button (or swiping away the modal).
  const [convoMode, setConvoMode] = useState(false);  // overlay visible
  const [convoState, setConvoState] = useState("idle"); // idle | listening | thinking | speaking
  const [convoInterim, setConvoInterim] = useState(""); // live partial transcript
  const [convoError, setConvoError] = useState("");
  const convoActiveRef = useRef(false); // used by audio callbacks to decide whether to re-listen
  // Voice-activity analyser — lets the orb rings scale to the user's
  // voice volume in real time so the listener has feedback that the app
  // is actually hearing them.
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const rafRef = useRef(0);
  const orbRef = useRef(null); // the <div.rm-convo-orb> — target of the --rm-convo-voice custom prop
  // How many consecutive empty recognition rounds we've seen. After 2 in
  // a row (= no speech detected twice), we stop the beep loop and show a
  // "Tap to speak" button instead of silently restarting forever.
  const emptyStreakRef = useRef(0);
  // Whisper-based recording state. We drive a MediaRecorder off the
  // shared mic stream while the VAD meter watches for (a) at least
  // ~350ms of voice activity to confirm the user spoke, and (b) ~1.4s
  // of subsequent silence to decide they're done. Then we stop the
  // recorder, POST the blob to /api/transcribe/audio, and feed the
  // returned text into sendMessage.
  const mediaRecorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const speechDetectedRef = useRef(false); // flipped true the first frame VAD > threshold
  const silenceStartRef = useRef(0);       // timestamp of first silent frame after speech
  const recordingActiveRef = useRef(false);
  const speechStartTimestampRef = useRef(0); // when VAD first exceeded threshold
  // Barge-in state — true while assistant TTS audio is actively playing
  // in convo mode. bargeStartRef is the first-frame-over-threshold
  // timestamp; when it exceeds BARGE_MIN_SPEECH_MS we trigger the
  // interrupt. The onBargeInRef lets the sendMessage closure register
  // its own cancellation hook (bumps the TTS gen + clears its queue).
  const ttsPlayingConvoRef = useRef(false);
  const bargeStartRef = useRef(0);
  const onBargeInRef = useRef(null);
  // Refs kept in sync with state so recognition callbacks (which closure
  // over the initial state at recognition.start()) see current values.
  const sendingRef = useRef(sending);
  useEffect(() => { sendingRef.current = sending; }, [sending]);

  function browserSpeechSupported() {
    return typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  // Spin up a getUserMedia → AudioContext → AnalyserNode chain so the
  // orb rings can react to the user's voice in real time. In conversation
  // mode, the SAME audio stream is ALSO fed into a MediaRecorder, so
  // silence-detection on the analyser decides when to stop recording
  // and ship the blob off to Whisper for transcription. This replaces
  // browser SpeechRecognition (which is flaky on Android Chrome / mobile
  // webviews / battery-saver mode) with the rock-solid Whisper pipeline
  // the Intake page already uses.
  async function startVoiceActivityMeter() {
    // If we already have a working analyser, just re-start the RAF loop.
    if (analyserRef.current) {
      runOrbMeter();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.65;
      source.connect(analyser);
      analyserRef.current = analyser;
      runOrbMeter();
    } catch (_e) {
      // Meter is optional — if mic permission is denied for the analyser
      // path, the recording path fails too; we surface a clear error.
      setConvoError(
        "I can't reach your microphone. Tap the lock icon next to the URL, allow the microphone, and try again."
      );
      convoActiveRef.current = false;
      setConvoState("idle");
    }
  }

  // Pull a frequency snapshot ~60fps, derive a 0..1 voice-energy value,
  // push it to the orb, AND — during active recording — run the
  // end-of-speech state machine that stops the recorder after a pause.
  function runOrbMeter() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const speechStart = 3;
    const speechEnd = 36;
    // Energy thresholds (0..1 space). SPEECH_ON is the trigger that
    // confirms the user is actually speaking; SILENCE_OFF is the ceiling
    // under which we consider them done.
    const SPEECH_ON = 0.14;
    const SILENCE_OFF = 0.08;
    // Require 220ms of voice OVER threshold before we'll count "heard
    // something" — prevents a cough from triggering a send.
    const MIN_SPEECH_MS = 220;
    // 1000ms of silence after speech = end-of-utterance. The user
    // explicitly asked for 1 second of grace so the mic doesn't
    // cut them off mid-explanation when they pause to think. The
    // orb shows a circular countdown ring that fills as this
    // timer elapses, so the user can see the OS is still listening.
    const SILENCE_TIMEOUT_MS = 1000;
    // ── Barge-in (user interrupts the assistant mid-playback) ──
    // Thresholds deliberately higher than the recording-mode VAD because
    // TTS audio played through the device speaker can bleed into the mic
    // even with echoCancellation on. A bigger energy floor + longer
    // confirmation window (450ms) rejects that bleed but still catches
    // the user actually trying to talk over the assistant.
    const BARGE_SPEECH_ON = 0.24;
    const BARGE_MIN_SPEECH_MS = 450;

    const tick = () => {
      if (!convoActiveRef.current) return;
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      let max = 0;
      for (let i = speechStart; i < speechEnd; i++) {
        sum += buf[i];
        if (buf[i] > max) max = buf[i];
      }
      const mean = sum / (speechEnd - speechStart);
      const norm = Math.min(1, Math.max(0, (Math.sqrt(mean / 255) * 1.4) - 0.05));
      if (orbRef.current) {
        orbRef.current.style.setProperty("--rm-convo-voice", norm.toFixed(3));
        orbRef.current.style.setProperty("--rm-convo-voice-peak", (max / 255).toFixed(3));
      }

      // End-of-speech detection — only runs when we're actively recording.
      if (recordingActiveRef.current) {
        const now = performance.now();
        if (norm > SPEECH_ON) {
          // Voice present.
          if (!speechDetectedRef.current) {
            if (!speechStartTimestampRef.current) {
              speechStartTimestampRef.current = now;
            } else if (now - speechStartTimestampRef.current > MIN_SPEECH_MS) {
              speechDetectedRef.current = true;
            }
          }
          silenceStartRef.current = 0; // reset silence clock whenever voice returns
          // Voice is back — clear the countdown ring instantly.
          if (orbRef.current) {
            orbRef.current.style.setProperty("--rm-convo-silence-deg", "0deg");
            orbRef.current.style.setProperty("--rm-convo-silence-alpha", "0");
          }
        } else if (norm < SILENCE_OFF && speechDetectedRef.current) {
          // Silence after confirmed speech — start / check the timer.
          if (!silenceStartRef.current) {
            silenceStartRef.current = now;
          } else if (now - silenceStartRef.current > SILENCE_TIMEOUT_MS) {
            stopWhisperRecording("silence");
          }
          // Drive the visual countdown ring (0 → 1 across the
          // SILENCE_TIMEOUT_MS window) so the user can see the OS
          // is still listening and how much grace they have left.
          if (orbRef.current) {
            const elapsed = silenceStartRef.current ? now - silenceStartRef.current : 0;
            const progress = Math.max(0, Math.min(1, elapsed / SILENCE_TIMEOUT_MS));
            orbRef.current.style.setProperty(
              "--rm-convo-silence-deg",
              `${(progress * 360).toFixed(2)}deg`,
            );
            orbRef.current.style.setProperty(
              "--rm-convo-silence-alpha",
              (progress * 0.95).toFixed(3),
            );
          }
        } else if (orbRef.current) {
          // Mid-band energy or no speech yet — keep the ring hidden.
          orbRef.current.style.setProperty("--rm-convo-silence-deg", "0deg");
          orbRef.current.style.setProperty("--rm-convo-silence-alpha", "0");
        }
      } else if (ttsPlayingConvoRef.current) {
        // Previously we did acoustic barge-in here (watch the mic for
        // the user cutting in over TTS). That was unreliable because
        // the device speaker bleeds into the mic, and users asked for
        // an explicit button instead. See rm-convo-interruptbtn in the
        // overlay below.
        bargeStartRef.current = 0;
      } else {
        bargeStartRef.current = 0;
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  // Start a new recording turn. Picks whatever codec the browser
  // supports (webm/opus on Android/desktop Chrome, mp4/aac on iOS
  // Safari), records from the shared mic stream, and sits waiting
  // for the VAD silence-detector in the orb loop to call stop.
  function startWhisperRecording() {
    if (!convoActiveRef.current) return;
    const stream = micStreamRef.current;
    if (!stream) return; // analyser hasn't initialized yet — caller will retry
    // Reset state machine.
    speechDetectedRef.current = false;
    speechStartTimestampRef.current = 0;
    silenceStartRef.current = 0;
    recorderChunksRef.current = [];
    if (orbRef.current) {
      orbRef.current.style.setProperty("--rm-convo-silence-deg", "0deg");
      orbRef.current.style.setProperty("--rm-convo-silence-alpha", "0");
    }
    setConvoInterim("");
    setConvoState("listening");
    setConvoError("");

    // Try supported MIME types in order — let the browser pick the first
    // one that actually works. Whisper accepts webm/opus + mp4/aac + m4a.
    const tryTypes = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    let mimeType = "";
    if (typeof window !== "undefined" && window.MediaRecorder) {
      for (const t of tryTypes) {
        if (window.MediaRecorder.isTypeSupported(t)) {
          mimeType = t;
          break;
        }
      }
    } else {
      setConvoError("Recording isn't supported in this browser. Try Chrome or Safari.");
      setConvoState("idle");
      return;
    }

    let mr;
    try {
      mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (e) {
      setConvoError(`Recorder error: ${e?.message || e}`);
      setConvoState("idle");
      return;
    }
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) recorderChunksRef.current.push(ev.data);
    };
    mr.onstop = async () => {
      recordingActiveRef.current = false;
      const chunks = recorderChunksRef.current;
      recorderChunksRef.current = [];
      if (!convoActiveRef.current) return;
      // If we never detected speech, loop the empty-streak logic.
      if (!speechDetectedRef.current) {
        emptyStreakRef.current = (emptyStreakRef.current || 0) + 1;
        if (emptyStreakRef.current >= 2) {
          setConvoError("I didn't hear anything. Tap the button below to try again.");
          setConvoState("idle");
          return;
        }
        // Restart listening.
        setTimeout(() => startWhisperRecording(), 250);
        return;
      }
      // Build the blob and upload to Whisper.
      const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
      if (blob.size < 1200) {
        // Absolute silence / almost-empty audio — treat as empty.
        emptyStreakRef.current = (emptyStreakRef.current || 0) + 1;
        if (emptyStreakRef.current >= 2) {
          setConvoError("I didn't hear anything. Tap the button below to try again.");
          setConvoState("idle");
          return;
        }
        setTimeout(() => startWhisperRecording(), 250);
        return;
      }
      setConvoState("thinking");
      try {
        const form = new FormData();
        const ext = (mr.mimeType || "").includes("mp4") ? "m4a" : "webm";
        form.append("file", blob, `chat_turn.${ext}`);
        const { data } = await axios.post(`${API}/transcribe/audio`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const text = (data?.text || "").trim();
        if (!text) {
          emptyStreakRef.current = (emptyStreakRef.current || 0) + 1;
          if (emptyStreakRef.current >= 2) {
            setConvoError("I couldn't make out what you said. Tap the button below to try again.");
            setConvoState("idle");
            return;
          }
          setTimeout(() => startWhisperRecording(), 250);
          return;
        }
        emptyStreakRef.current = 0;
        // In hands-free convo mode we deliberately skip surfacing the
        // user's transcription — a more natural "I spoke, it answered"
        // flow. Outside convo mode the caller shows nothing anyway.
        sendMessage(text);
      } catch (err) {
        setConvoError(
          err?.response?.data?.detail
            ? `Transcription failed: ${err.response.data.detail}`
            : "Couldn't reach the transcription service. Tap the button below to retry."
        );
        setConvoState("idle");
      }
    };

    try {
      // 250ms chunks — gives the upload a full, valid container if the
      // recorder is stopped very early.
      mr.start(250);
      recordingActiveRef.current = true;
    } catch (e) {
      setConvoError(`Recorder could not start: ${e?.message || e}`);
      setConvoState("idle");
    }
  }

  // Called by the VAD silence detector, the "End conversation" button,
  // and (defensively) by openConvoMode before reopening.
  function stopWhisperRecording(_reason) {
    const mr = mediaRecorderRef.current;
    if (!mr) {
      recordingActiveRef.current = false;
      return;
    }
    mediaRecorderRef.current = null;
    recordingActiveRef.current = false;
    try {
      if (mr.state !== "inactive") mr.stop();
    } catch { /* noop */ }
  }

  // Legacy alias so existing call sites (TTS onended, openConvoMode,
  // retry button) don't need to change.
  function startRecognition() {
    startWhisperRecording();
  }

  // Called by the big Interrupt button in the voice overlay. Stops
  // the currently-playing TTS, invalidates any in-flight TTS fetches +
  // queued audio (the sendMessage closure registered a cancellation
  // hook in onBargeInRef), and immediately reopens the mic so the
  // user can speak their next turn.
  function interruptAssistant() {
    if (!convoActiveRef.current) return;
    ttsPlayingConvoRef.current = false;
    if (onBargeInRef.current) {
      try { onBargeInRef.current(); } catch { /* noop */ }
      onBargeInRef.current = null;
    }
    stopSpeaking();
    startWhisperRecording();
  }

  function stopVoiceActivityMeter() {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch { /* noop */ }
      micStreamRef.current = null;
    }
    // Reset the CSS var so the orb returns to its default steady pulse.
    if (orbRef.current) {
      orbRef.current.style.removeProperty("--rm-convo-voice");
      orbRef.current.style.removeProperty("--rm-convo-voice-peak");
      orbRef.current.style.removeProperty("--rm-convo-silence-deg");
      orbRef.current.style.removeProperty("--rm-convo-silence-alpha");
    }
  }

  // (Previously this file had a SpeechRecognition-based startRecognition
  // that was flaky on mobile Chrome — results frequently never flipped
  // isFinal before silence auto-ended recognition, which made the voice
  // chat go beep-beep-beep without ever responding. It's been replaced
  // by the MediaRecorder + Whisper flow above, which works everywhere
  // getUserMedia works.)


  // Pre-primed audio element for convo-mode TTS playback. iOS Safari
  // and mobile Chrome only allow `audio.play()` to fire programmatically
  // if the audio element was first interacted with during a user
  // gesture. In convo mode, the user gesture is the phone-icon tap;
  // by the time the TTS audio arrives (after Whisper + LLM round-trip,
  // often several seconds later), we're no longer "inside" that
  // gesture. The fix is to create + prime an audio element DURING
  // the tap and then reuse it for every TTS chunk — the browser
  // remembers the element was unlocked by the original gesture.
  const primedAudioRef = useRef(null);

  function openConvoMode() {
    // Silence the global background music BEFORE we touch the mic so
    // the song doesn't bleed into Whisper's recording buffer.
    try { pauseMusic(); } catch { /* noop */ }
    // We no longer need SpeechRecognition — MediaRecorder + Whisper
    // works on any browser that supports getUserMedia, which is every
    // modern mobile + desktop browser. Keep a minimal MediaRecorder
    // guard just to surface a helpful error in ancient webviews.
    if (typeof window === "undefined" || !window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setConvoError(
        "Voice conversation isn't supported in this browser. Try Chrome or Safari."
      );
      setConvoMode(true);
      return;
    }
    stopSpeaking();
    emptyStreakRef.current = 0;
    convoActiveRef.current = true;
    setConvoMode(true);
    setConvoError("");
    setConvoState("listening");

    // ── Audio autoplay-policy primer (Feb 2026 fix for the
    // "won't speak back" bug on iOS Safari + mobile Chrome) ──
    // Create a reusable audio element WHILE we're still inside the
    // user-gesture frame from the phone-icon tap. Call .play() on
    // it once (with a silent data: URI) so the browser marks the
    // element as user-authorized. Subsequent TTS playbacks reuse
    // this same element by swapping its `src`, which keeps the
    // authorization alive across the Whisper + LLM round-trip.
    if (!primedAudioRef.current) {
      try {
        const a = new Audio();
        // 50-ms silent mp3 — tiny, valid, plays inaudibly to prime
        // the element. Source: standard "silent mp3" used by every
        // major web-audio library to bypass mobile autoplay locks.
        a.src =
          "data:audio/mp3;base64,/+MYxAAAAANIAUAAAASEEB/jwOFM/0MM/90b/+RhST" +
          "//w4NFwOjf///PZu////9lns5GFDv//l9GlUIEEIAAAgIg8Ir/JGq3/+MYxA" +
          "kT19SfABhEuP/zL8w/ZGYqIxgAJ8O+9G7BSXuY/86l9CzWqLkr/PZG9D8z" +
          "//+MYxBwT06qIAAJEuPMYa6kp///+ZG/+wAgRgBaQQwAAgAALdbHCvBJN" +
          "z19ld/+MYxC0Ti2qkAAYEuPI/0vw3//6bzZQOZWVz8/UmHv9hZS8Wl4xT" +
          "AgQYxBA";
        // Volume 0 so even if the primer ever audibles a click, it's
        // silent. We pause immediately after the play-promise resolves
        // — the element stays unlocked for the rest of the session.
        a.volume = 0;
        const p = a.play();
        if (p && typeof p.catch === "function") {
          p.then(() => { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } })
           .catch(() => { /* primer failed silently — fall back to per-chunk audio */ });
        }
        primedAudioRef.current = a;
      } catch {
        // primedAudioRef stays null — convo TTS falls back to
        // `new Audio(url)` per chunk (works on desktop, broken on iOS).
      }
    }

    // Start the VAD meter — this calls getUserMedia and stores the stream
    // in micStreamRef. When it's ready, kick off recording.
    (async () => {
      await startVoiceActivityMeter();
      if (convoActiveRef.current && micStreamRef.current) {
        startWhisperRecording();
      }
    })();
  }

  function closeConvoMode() {
    convoActiveRef.current = false;
    ttsPlayingConvoRef.current = false;
    bargeStartRef.current = 0;
    if (onBargeInRef.current) {
      try { onBargeInRef.current(); } catch { /* noop */ }
      onBargeInRef.current = null;
    }
    stopWhisperRecording("close");
    stopSpeaking();
    stopVoiceActivityMeter();
    setConvoInterim("");
    setConvoState("idle");
    setConvoMode(false);
    // Feb 2026: release the primed audio element so a future
    // openConvoMode() re-primes within a fresh user gesture (some
    // browsers expire the autoplay authorization after long idles).
    if (primedAudioRef.current) {
      try {
        primedAudioRef.current.pause();
        primedAudioRef.current.src = "";
      } catch { /* noop */ }
      primedAudioRef.current = null;
    }
  }

  // When an assistant reply lands during convo mode, flip state to
  // "speaking" so the modal animates the orb accordingly. The transition
  // to "listening" happens inside audio.onended in speakMessage().
  useEffect(() => {
    if (!convoMode) return;
    // Detect new assistant messages by length — last message arriving
    // flips us from thinking → speaking.
    const last = messages[messages.length - 1];
    if (last?.role === "assistant") {
      setConvoState("speaking");
    }
  }, [messages.length, convoMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure we clean up when leaving the page.
  useEffect(() => {
    return () => {
      convoActiveRef.current = false;
      stopWhisperRecording("unmount");
      stopVoiceActivityMeter();
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Bootstrap: resume prior session or start a new one.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const stored = (typeof window !== "undefined" && localStorage.getItem(SESSION_KEY)) || "";
      if (stored) {
        try {
          const { data } = await axios.get(`${API}/chat/session/${stored}`);
          if (!cancelled && data?.id) {
            setSessionId(data.id);
            setMessages((data.messages || []).map(({ role, content }) => ({ role, content })));
            return;
          }
        } catch {
          /* stored session expired or missing — fall through and create new */
        }
      }
      try {
        const { data } = await axios.post(`${API}/chat/start`, {});
        if (cancelled) return;
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      } catch (e) {
        if (!cancelled) setError("Could not start the conversation. Please refresh.");
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll to latest message whenever messages change or we're sending.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  async function sendMessage(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || !sessionId || sending) return;
    setError("");
    setInput("");
    // If the user sends a new message while audio is still playing,
    // stop it — feels natural.
    stopSpeaking();
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setSending(true);

    const inConvo = convoActiveRef.current;
    const shouldSpeak = autoSpeakRef.current || inConvo;

    // Claim a generation number for this entire turn. Every TTS fetch
    // and every audio playback captures this gen — if the user starts
    // a new turn mid-stream, stopSpeaking() bumps ttsGenRef and all
    // in-flight stuff drops on the floor instead of talking over the
    // new reply.
    const turnGen = ++ttsGenRef.current;

    // Index of the assistant bubble we're about to grow. We append an
    // empty assistant message up front so the user sees the "thinking"
    // indicator transition smoothly into streamed text.
    let assistantIdx = 0;
    setMessages((prev) => {
      assistantIdx = prev.length;
      return [...prev, { role: "assistant", content: "" }];
    });

    // ── Ordered TTS queue for this turn ──────────────────────────
    // Each entry is a Promise that resolves to { audio, url } for one
    // sentence. We consume the queue strictly in order so sentences
    // never overlap, even if the TTS round-trips arrive out of order.
    const ttsPromises = [];        // Array<Promise<{audio,url}|null>>
    let playIdx = 0;               // which promise we're playing next
    let isPlaying = false;
    let streamFinished = false;

    const drainQueue = () => {
      if (turnGen !== ttsGenRef.current) return; // superseded turn
      if (isPlaying) return;
      if (playIdx >= ttsPromises.length) {
        // Queue empty right now. If the stream is finished AND we're
        // in convo mode, reopen the mic for the next turn.
        if (streamFinished && inConvo && convoActiveRef.current) {
          setTimeout(() => startWhisperRecording(), 180);
        }
        return;
      }
      isPlaying = true;
      const currentSlot = playIdx++;
      ttsPromises[currentSlot]
        .then((item) => {
          if (!item || turnGen !== ttsGenRef.current) {
            isPlaying = false;
            drainQueue();
            return;
          }
          // Convo mode reuses a single primed Audio element so iOS
          // Safari + mobile Chrome honor the autoplay authorization
          // from the original phone-icon tap. Swap the `src` HERE
          // (not in the Promise body) so we never interrupt a
          // chunk that is currently playing — the previous chunk's
          // onended → cleanup has already fired by the time we
          // reach this point.
          const url = item.url;
          let audio;
          if (inConvo && primedAudioRef.current) {
            audio = primedAudioRef.current;
            try { audio.pause(); } catch { /* noop */ }
            audio.volume = 1;
            audio.src = url;
            try { audio.load(); } catch { /* noop */ }
          } else {
            audio = new Audio(url);
          }
          audioRef.current = audio;
          setPlayingIdx(assistantIdx);
          if (inConvo) ttsPlayingConvoRef.current = true;
          const cleanup = () => {
            if (audioRef.current === audio) {
              audioRef.current = null;
              setPlayingIdx(-1);
            }
            try { URL.revokeObjectURL(url); } catch { /* noop */ }
            if (inConvo) ttsPlayingConvoRef.current = false;
            isPlaying = false;
            drainQueue();
          };
          audio.onended = cleanup;
          audio.onerror = cleanup;
          audio.play().catch(cleanup);
        })
        .catch(() => {
          isPlaying = false;
          drainQueue();
        });
    };

    const enqueueSentenceTTS = (sentence) => {
      if (!shouldSpeak) return;
      const clean = (sentence || "").replace(/\*+/g, "").trim();
      if (!clean) return;
      // Fetch the TTS audio here; defer attaching it to the primed
      // Audio element until drainQueue is ready to play this chunk
      // (otherwise a fast follow-up chunk could swap `audio.src`
      // mid-playback and cut the current word off — Feb 2026 fix).
      const p = (async () => {
        try {
          const resp = await axios.post(
            `${API}/chat/tts`,
            { text: clean, voice: ttsVoice },
            { responseType: "blob" }
          );
          if (turnGen !== ttsGenRef.current) return null;
          const url = URL.createObjectURL(resp.data);
          return { url };
        } catch {
          return null;
        }
      })();
      ttsPromises.push(p);
      drainQueue();
    };

    // ── Streamed chat response ──────────────────────────────────
    // First TTS chunk fires on the earliest clause break (, ; : — . ! ?)
    // after 5+ chars so audio starts playing as soon as possible.
    // Subsequent chunks use full-sentence boundaries (.!?) for natural
    // prosody. The shorter first-chunk threshold is a no-cost speed win:
    // OpenAI TTS round-trip is ~600-800ms, so cutting the first chunk
    // from 8 chars to 5 chars saves ~80-150ms of perceived latency.
    let accumulated = "";
    let unspokenTail = "";
    let firstSpoken = false;
    const FAST_FIRST_RE = /^([^.!?,;:—]{5,}?[.!?,;:—])(\s|$)/;
    const SENTENCE_RE = /^([^.!?]+[.!?]+)(\s|$)/;

    const flushSentences = (force = false) => {
      while (true) {
        const re = firstSpoken ? SENTENCE_RE : FAST_FIRST_RE;
        const m = unspokenTail.match(re);
        if (!m) break;
        const sentence = m[1].trim();
        unspokenTail = unspokenTail.slice(m[0].length);
        if (sentence) {
          enqueueSentenceTTS(sentence);
          firstSpoken = true;
        }
      }
      if (force) {
        const remainder = unspokenTail.trim();
        unspokenTail = "";
        if (remainder) enqueueSentenceTTS(remainder);
      }
    };

    // Register this turn's cancellation hook for barge-in. When the user
    // interrupts, triggerBargeIn() calls this to stop the queue from
    // firing any more TTS or playing anything already queued.
    onBargeInRef.current = () => {
      // Bumping the gen is enough — all in-flight TTS fetches and their
      // audios check `turnGen !== ttsGenRef.current` and bail.
      ttsGenRef.current += 1;
      streamFinished = true;
    };

    try {
      const resp = await fetch(`${API}/chat/message/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText || `Chat stream failed (${resp.status})`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalReply = "";
      let errorMsg = "";
      // Read loop — parse SSE events as they arrive.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 2);
          if (!raw.startsWith("data:")) continue;
          const payload = raw.slice(5).trim();
          if (!payload) continue;
          let evt;
          try { evt = JSON.parse(payload); } catch { continue; }
          if (evt.delta) {
            accumulated += evt.delta;
            unspokenTail += evt.delta;
            const snapshot = accumulated;
            setMessages((prev) => {
              if (prev.length <= assistantIdx) return prev;
              const next = prev.slice();
              next[assistantIdx] = { role: "assistant", content: snapshot };
              return next;
            });
            flushSentences(false);
          }
          if (evt.done) {
            finalReply = evt.reply || accumulated;
            if (finalReply !== accumulated) {
              setMessages((prev) => {
                if (prev.length <= assistantIdx) return prev;
                const next = prev.slice();
                next[assistantIdx] = { role: "assistant", content: finalReply };
                return next;
              });
            }
            flushSentences(true);
          }
          if (evt.error) {
            errorMsg = evt.error;
          }
        }
      }
      streamFinished = true;
      if (errorMsg) throw new Error(errorMsg);
      // If nothing needed to be spoken (voice off AND not convo mode),
      // we're done.  If convo mode but shouldSpeak was false — never
      // actually happens because shouldSpeak = auto || inConvo — but
      // just in case, reopen mic.
      if (!shouldSpeak && inConvo && convoActiveRef.current) {
        setTimeout(() => startWhisperRecording(), 180);
      } else {
        // Kick drain one more time in case the queue was empty
        // when streamFinished flipped true (no voice mode).
        drainQueue();
      }
    } catch (e) {
      streamFinished = true;
      setError(
        e?.message
          ? `Couldn't reach the OS: ${e.message}`
          : "Something went quiet. Try again in a moment."
      );
      if (convoActiveRef.current) {
        setTimeout(() => startWhisperRecording(), 800);
      }
    } finally {
      setSending(false);
      // Clear barge-in hook once this turn is over (the ref is replaced
      // by the next turn's sendMessage anyway, but clearing early avoids
      // stale closures firing after the fact).
      if (onBargeInRef.current) onBargeInRef.current = null;
    }
  }

  async function newConversation() {
    stopSpeaking();
    if (sessionId) {
      try {
        await axios.delete(`${API}/chat/session/${sessionId}`);
      } catch {
        /* non-fatal */
      }
    }
    localStorage.removeItem(SESSION_KEY);
    setMessages([]);
    setInput("");
    setError("");
    setPacketCtx(null);
    try {
      const { data } = await axios.post(`${API}/chat/start`, {});
      setSessionId(data.session_id);
      localStorage.setItem(SESSION_KEY, data.session_id);
    } catch {
      setError("Could not start a new conversation. Please refresh.");
    }
  }

  // Pull saved packet context from the server when the session loads or
  // changes — keeps the header badge accurate after refresh.
  useEffect(() => {
    let cancelled = false;
    async function loadCtx() {
      if (!sessionId) return;
      try {
        const { data } = await axios.get(`${API}/chat/session/${sessionId}`);
        if (cancelled) return;
        const ctx = data?.packet_context;
        if (ctx?.name || ctx?.prior_rung || ctx?.filename) {
          setPacketCtx({
            name: ctx.name || "",
            rung: ctx.prior_rung || "",
            filename: ctx.filename || "",
          });
        } else {
          setPacketCtx(null);
        }
      } catch {
        /* non-fatal */
      }
    }
    loadCtx();
    return () => { cancelled = true; };
  }, [sessionId]);

  async function onPacketFileChosen(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of the same filename
    if (!file || !sessionId) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a Restoration Matrix packet PDF.");
      return;
    }
    setUploadingPacket(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/chat/upload-packet?session_id=${encodeURIComponent(sessionId)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPacketCtx({
        name: data.name || "",
        rung: data.rung || "",
        filename: data.filename || file.name,
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Could not read that packet. Please try a different PDF."
      );
    } finally {
      setUploadingPacket(false);
    }
  }

  async function clearPacketContext() {
    if (!sessionId) return;
    try {
      await axios.delete(`${API}/chat/session/${sessionId}/packet`);
    } catch {
      /* non-fatal */
    }
    setPacketCtx(null);
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="chat-root">
      <PageMeta
        title="Talk with the OS"
        description="Ask any question and hear the Restoration Matrix answer in a gentle teacher's voice — Christ-centered pastoral conversation with discernment beneath every word."
        path="/chat"
      />
      {/* Header */}
      <header className="rm-chat-header">
        <div className="rm-chat-header-inner">
          <Link
            to="/"
            className="rm-chat-back"
            data-testid="chat-back-link"
            aria-label="Back to home"
          >
            ← The Restoration Matrix
          </Link>
          <div className="rm-chat-header-actions">
            <input
              type="file"
              accept="application/pdf,.pdf"
              ref={packetFileRef}
              onChange={onPacketFileChosen}
              style={{ display: "none" }}
              data-testid="chat-packet-file-input"
            />
            <button
              type="button"
              onClick={() => packetFileRef.current?.click()}
              className="rm-chat-uploadbtn"
              data-testid="chat-upload-packet-btn"
              disabled={uploadingPacket || !sessionId}
              title="Upload your Restoration Matrix packet so the OS can speak with full awareness of your story"
              aria-label="Upload your packet"
            >
              {uploadingPacket ? "Reading…" : "📄 Upload packet"}
            </button>
            <button
              type="button"
              onClick={toggleAutoSpeak}
              className={`rm-chat-voicebtn ${autoSpeak ? "rm-chat-voicebtn-on" : ""}`}
              data-testid="chat-auto-speak-toggle"
              title={autoSpeak ? "Tap to mute the voice" : "Tap to hear the OS read replies aloud"}
              aria-label={autoSpeak ? "Voice on — tap to mute" : "Voice off — tap to enable"}
            >
              {autoSpeak ? "🔊 Voice on" : "🔇 Voice off"}
            </button>
            <label
              className="rm-chat-voicepicker"
              data-testid="chat-voice-picker-label"
              title="Choose the voice the OS speaks in"
            >
              <span className="rm-chat-voicepicker-label">Voice</span>
              <select
                value={ttsVoice}
                onChange={(e) => changeTtsVoice(e.target.value)}
                className="rm-chat-voicepicker-select"
                data-testid="chat-voice-picker"
                aria-label="Choose the voice the OS speaks in"
              >
                {TTS_VOICE_OPTIONS.map((v) => (
                  <option
                    key={v.id}
                    value={v.id}
                    data-testid={`chat-voice-option-${v.id}`}
                  >
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={newConversation}
              className="rm-chat-newbtn"
              data-testid="chat-new-conversation-btn"
            >
              New conversation
            </button>
          </div>
        </div>
        {packetCtx && (
          <div className="rm-chat-packet-badge" data-testid="chat-packet-badge">
            <span className="rm-chat-packet-dot" aria-hidden="true" />
            <span className="rm-chat-packet-label">
              Walking with{packetCtx.name ? ` ${packetCtx.name}` : ""}
              {packetCtx.rung ? ` · on ${packetCtx.rung}` : ""}
            </span>
            <button
              type="button"
              onClick={clearPacketContext}
              className="rm-chat-packet-clear"
              data-testid="chat-packet-clear-btn"
              aria-label="Remove packet context"
              title="Remove packet context"
            >
              ×
            </button>
          </div>
        )}
        <div className="rm-chat-title">
          <div className="rm-chat-overline">Talk with the OS</div>
          <h1 className="rm-chat-h1">A quiet conversation with the Matrix.</h1>
          <p className="rm-chat-sub">
            Ask about the ladder, the work of forgiveness, the rung you're on, or anything the OS teaches.
            Not medical advice. Not therapy. A companion on the road.
          </p>
          <p className="rm-chat-sub rm-chat-sub-voicehint" data-testid="chat-voicehint">
            <span className="rm-chat-voicehint-inner">
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rm-chat-voicehint-icon">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span>Tap the phone icon below to <em>actually talk</em> with the OS — hands-free voice, not just texting.</span>
            </span>
          </p>
          <div className="rm-chat-privacy">
            <span className="rm-privacy-inline" data-testid="chat-privacy-disclaimer">
              <span className="rm-privacy-dot" aria-hidden="true" />
              Privacy guaranteed — no record of this conversation is saved, no account needed.
            </span>
          </div>
        </div>
      </header>

      {/* Message log */}
      <main className="rm-chat-main" ref={scrollRef} data-testid="chat-log">
        <div className="rm-chat-log-inner">
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
              className={`rm-chat-row ${m.role === "user" ? "rm-chat-row-user" : "rm-chat-row-os"}`}
              data-testid={`chat-message-${m.role}`}
            >
              <div className="rm-chat-role">{m.role === "user" ? "You" : "The OS"}</div>
              <div
                className={`rm-chat-bubble ${
                  m.role === "assistant" && playingIdx === i ? "rm-chat-bubble-speaking" : ""
                }`}
              >
                {renderReply(m.content)}
              </div>
              {m.role === "assistant" && (
                <button
                  type="button"
                  onClick={() =>
                    playingIdx === i ? stopSpeaking() : speakMessage(i, m.content)
                  }
                  className={`rm-chat-play-btn ${playingIdx === i ? "rm-chat-play-btn-active" : ""}`}
                  data-testid={`chat-play-btn-${i}`}
                  aria-label={playingIdx === i ? "Stop playback" : "Read this reply aloud"}
                  title={playingIdx === i ? "Stop" : "Read aloud"}
                >
                  {playingIdx === i ? "⏸ Stop" : "🔊 Read aloud"}
                </button>
              )}
            </motion.div>
          ))}

          {sending && (
            <div className="rm-chat-row rm-chat-row-os" data-testid="chat-typing">
              <div className="rm-chat-role">The OS</div>
              <div className="rm-chat-bubble rm-chat-typing">
                <span className="rm-chat-dot" />
                <span className="rm-chat-dot" />
                <span className="rm-chat-dot" />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Composer */}
      <footer className="rm-chat-composer">
        {error && (
          <div className="rm-chat-error" data-testid="chat-error">
            {error}
          </div>
        )}

        <div className="rm-chat-input-row">
          <button
            type="button"
            onClick={openConvoMode}
            className="rm-chat-callbtn"
            data-testid="chat-open-conversation-btn"
            title="Start a hands-free voice call with the OS"
            aria-label="Start voice call"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span className="rm-chat-callbtn-label">Voice call</span>
          </button>
          <div className="rm-chat-mic-wrap">
            <MicDictation
              value={input}
              onTranscript={(txt) => setInput(txt)}
              onActivate={pauseMusic}
              testId="chat-mic-btn"
              title="Tap to speak to the OS"
            />
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask the OS, or tap the mic to speak…"
            rows={1}
            className="rm-chat-textarea"
            data-testid="chat-input"
            disabled={!sessionId || sending}
            maxLength={4000}
          />
          <button
            type="button"
            onClick={() => sendMessage()}
            disabled={!input.trim() || !sessionId || sending}
            className="rm-chat-send-btn"
            data-testid="chat-send-btn"
            aria-label="Send message"
          >
            Send
          </button>
        </div>
      </footer>

      {/* Voice Conversation overlay — hands-free continuous dialogue. */}
      {convoMode && (
        <div className="rm-convo-overlay" data-testid="chat-convo-overlay" role="dialog" aria-modal="true">
          <button
            type="button"
            onClick={closeConvoMode}
            className="rm-convo-close"
            data-testid="chat-convo-close"
            aria-label="End voice conversation"
          >
            ×
          </button>
          <div className="rm-convo-inner">
            <div ref={orbRef} className={`rm-convo-orb rm-convo-orb-${convoState}`} aria-hidden="true">
              <div className="rm-convo-orb-core" />
              <div className="rm-convo-orb-ring rm-convo-orb-ring-1" />
              <div className="rm-convo-orb-ring rm-convo-orb-ring-2" />
              <div className="rm-convo-orb-ring rm-convo-orb-ring-3" />
              <div className="rm-convo-orb-countdown" data-testid="chat-convo-silence-countdown" />
            </div>
            <div className="rm-convo-label" data-testid="chat-convo-label">
              {convoState === "listening" && "I'm listening…"}
              {convoState === "thinking" && "Thinking with you…"}
              {convoState === "speaking" && "Reading back to you…"}
              {convoState === "idle" && "Getting ready…"}
            </div>
            {convoInterim && (
              <div className="rm-convo-interim" data-testid="chat-convo-interim">
                {convoInterim}
              </div>
            )}
            {convoError && (
              <div className="rm-convo-error" data-testid="chat-convo-error">
                {convoError}
              </div>
            )}
            {convoState === "idle" && convoMode && (
              <button
                type="button"
                onClick={() => {
                  emptyStreakRef.current = 0;
                  setConvoError("");
                  convoActiveRef.current = true;
                  startVoiceActivityMeter();
                  startRecognition();
                }}
                className="rm-convo-retrybtn"
                data-testid="chat-convo-retry-btn"
              >
                🎤 Tap to speak
              </button>
            )}
            {/* Interrupt button — visible only while the OS is talking
               or about to talk. Stops TTS instantly and reopens the
               mic so the user can jump in. More reliable than acoustic
               barge-in since the device speaker used to bleed into the
               mic and trigger false interrupts. */}
            {convoMode && (convoState === "speaking" || convoState === "thinking") && (
              <button
                type="button"
                onClick={interruptAssistant}
                className="rm-convo-interruptbtn"
                data-testid="chat-convo-interrupt-btn"
                aria-label="Interrupt the OS and speak now"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                <span>Interrupt &amp; speak</span>
              </button>
            )}
            <div className="rm-convo-hint">
              Speak when you're ready. I'll listen until you pause, then read my answer back to you.
            </div>
            <button
              type="button"
              onClick={closeConvoMode}
              className="rm-convo-endbtn"
              data-testid="chat-convo-end-btn"
            >
              End conversation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
